import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { resolveStateKey } from '../lib/us-states';
import { filterForums, forumToValueType, useStateTerminology } from '../lib/stateTerminology';
import { Input, Select, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * AddValueDialog — the single entry point for keying a value on a parcel
 * (appeal-lifecycle respec §4.2). Replaces the generic
 * `CreateEntityModal table="parcel_values"` create modal with a bespoke dialog
 * that models a value AS an appeal event:
 *
 *  - an Appeal selector (an existing appeal for the parcel, or "+ New appeal"
 *    which reveals coverage-year checkboxes → appeals.covered_tax_years);
 *  - Forum + Event selects sourced from the state's stage template
 *    (GET /api/org-settings/stage-templates, keyed by the parcel's state), narrowed to
 *    the value types the state's rule allows (GET /api/terminology/:state, spec 0012);
 *  - a Source (required, → value_source) + Date;
 *  - a Market / Ratio / Assessed grid that computes BOTH directions
 *    (assessed = market × ratio; market = assessed ÷ ratio) with a blended-ratio
 *    keying check on the total.
 *
 * On save it POSTs parcel_values with appeal_id + forum + event + value_source +
 * the grid, so the existing value→tax cascade fires unchanged. For "+ New appeal"
 * it first creates the appeal via POST /api/workflow/file-appeal/:parcelId (which
 * keeps the one-appeal-per-year semantics and returns the appeal id), then stamps
 * that id onto the value.
 */

/** The stored stage-templates shape (GET /api/org-settings/stage-templates). */
interface StageEvent { name: string; notifiable?: boolean }
interface StageForum { name: string; events: StageEvent[]; retired?: boolean }
interface StageStateTemplate { forums: StageForum[] }
type StageTemplates = Record<string, StageStateTemplate>;

export interface AddValueAppealOption {
  id: string;
  tax_year: number | null;
  lifecycle_stage: string;
}

export interface AddValueDialogProps {
  open: boolean;
  onClose: () => void;
  parcelId: string;
  /** The tax year the value is keyed for (the parcel's current year). */
  taxYear: number;
  /** The parcel's state postal/name — selects the stage-template ladder. */
  parcelState: string | null;
  /** Existing appeals on the parcel, for the appeal selector. */
  appeals: AddValueAppealOption[];
  /**
   * Retained for callers; no longer read. The "+ New appeal" option it gated is gone —
   * adding a value is adding the next STAGE of an appeal already in flight, so the dialog
   * never files one. Filing stays on the parcel header's "File appeal" action.
   */
  canCreateAppeal?: boolean;
  /**
   * The most recent value record on this parcel-year, if any. Adding a value is adding
   * the NEXT STAGE of the same appeal, so its figures seed the grid and the operator
   * edits only what actually moved (operator feedback 8/6).
   */
  priorValue?: PriorValue | null;
  invalidateKeys?: unknown[][];
}

/** The subset of a parcel_values row the grid inherits from. */
export interface PriorValue {
  market_land?: number | string | null;  market_imp?: number | string | null;  market_pp?: number | string | null;
  ratio_land?: number | string | null;   ratio_imp?: number | string | null;   ratio_pp?: number | string | null;
  assessed_land?: number | string | null; assessed_imp?: number | string | null; assessed_pp?: number | string | null;
}


// The forum → value_type slot map lives in lib/stateTerminology (forumToValueType), shared with
// the value's own detail page. A UNIQUE(parcel_id, tax_year, value_type) collision (two events
// keyed to the same slot in one year) is surfaced as a friendly message on save.

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n: number): string => `$${Math.round(n).toLocaleString()}`;

/** One component row of the Market/Ratio/Assessed grid. */
interface GridRow { market: string; ratio: string; assessed: string }
const emptyRow = (): GridRow => ({ market: '', ratio: '1', assessed: '' });

/**
 * Seed one grid row from a prior value record.
 *
 * (Operator feedback 8/6) Adding a value is adding the NEXT STAGE of the same appeal, so
 * the figures carry forward from the previous record and the operator edits what moved —
 * rather than re-keying market, ratio and assessed from scratch every stage.
 *
 * Blank rather than 0 for an absent component: an empty box invites a value, whereas a
 * pre-filled 0 reads as a stated figure and would quietly become one on save.
 */
function rowFromPrior(market: unknown, ratio: unknown, assessed: unknown): GridRow {
  const str = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(v));
  return { market: str(market), ratio: str(ratio) || '1', assessed: str(assessed) };
}

export function AddValueDialog({
  open, onClose, parcelId, taxYear, parcelState, appeals, priorValue = null, invalidateKeys = [],
}: AddValueDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [forum, setForum] = useState('');
  const [event, setEvent] = useState('');
  const [source, setSource] = useState('');
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  // Grid rows keyed by component. `emptyRow` seeds ratio at 1 (100%).
  const [land, setLand] = useState<GridRow>(emptyRow);
  const [imp, setImp] = useState<GridRow>(emptyRow);
  const [pp, setPp] = useState<GridRow>(emptyRow);
  // Seed the grid from the previous stage each time the dialog OPENS. Keyed on `open` so
  // reopening after a save picks up the record just written, and so an operator's in-progress
  // edits are never overwritten mid-session by a background refetch.
  const [seededFor, setSeededFor] = useState<PriorValue | null | undefined>(undefined);
  if (open && seededFor !== priorValue) {
    setSeededFor(priorValue);
    if (priorValue) {
      setLand(rowFromPrior(priorValue.market_land, priorValue.ratio_land, priorValue.assessed_land));
      setImp(rowFromPrior(priorValue.market_imp, priorValue.ratio_imp, priorValue.assessed_imp));
      setPp(rowFromPrior(priorValue.market_pp, priorValue.ratio_pp, priorValue.assessed_pp));
    }
  }

  // Stage templates drive the Forum + Event selects, keyed by the parcel's state.
  const templatesQuery = useQuery<StageTemplates>({
    queryKey: ['org-settings', 'stage-templates'],
    queryFn: ({ signal }) => api.get<StageTemplates>('/api/org-settings/stage-templates', signal),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // Resolve the state's ladder. Templates are keyed by full name ("Texas") while a
  // parcel carries a postal code ("TX"), so resolveStateKey matches either shape
  // against all 50 + DC — an org can add any state's ladder and it still resolves.
  const stateTemplate = useMemo<StageStateTemplate | null>(() => {
    const t = templatesQuery.data;
    if (!t) return null;
    const key = resolveStateKey(Object.keys(t), parcelState);
    return key ? t[key] : null;
  }, [templatesQuery.data, parcelState]);

  // Spec 0012: only the forums whose value-type slot the parcel's state allows. No allowlist
  // (or one that matches nothing here) offers the whole ladder.
  const terminology = useStateTerminology(parcelState, open).data;
  const forums = useMemo(
    () => filterForums((stateTemplate?.forums ?? []).filter((f) => !f.retired), terminology?.allowed_value_types),
    [stateTemplate, terminology],
  );
  const events = useMemo(
    () => forums.find((f) => f.name === forum)?.events ?? [],
    [forums, forum],
  );

  // Derived totals + blended ratio (the keying check). Blended ratio =
  // total assessed ÷ total market.
  const marketTotal = num(land.market) + num(imp.market) + num(pp.market);
  const assessedTotal = num(land.assessed) + num(imp.assessed) + num(pp.assessed);
  const blendedRatio = marketTotal > 0 ? assessedTotal / marketTotal : 0;

  const reset = () => {
    setForum(''); setEvent('');
    setSource(''); setDate(new Date().toISOString().slice(0, 10));
    setLand(emptyRow()); setImp(emptyRow()); setPp(emptyRow()); setSeededFor(undefined);
  };

  // The create echo carries `warnings` ONLY when the server has something to say —
  // today, the §5 "this looks out of order, saved as entered" note. The record is
  // already written by the time we see it, so it is reported, never blocking.
  const mutation = useMutation<{ warnings?: string[] } | unknown, Error, void>({
    mutationFn: async () => {
      // Resolve the appeal id. "+ New appeal" files the appeal first (one per year)
      // and stamps its covered years; an existing selection is used as-is; an empty
      // selection leaves the value unlinked (appeal_id is nullable).
      // DERIVED, not chosen: this year's appeal if the parcel has one, else its only
      // appeal, else null. `appeal_id` is nullable, so an unlinked stage still saves —
      // which is the point: nothing about the appeal may block recording what happened.
      const forYear = appeals.filter((a) => a.tax_year === taxYear);
      const appealId: string | null =
        forYear.length === 1 ? forYear[0].id
          : appeals.length === 1 ? appeals[0].id
            : null;
      const value_type = forum ? forumToValueType(forum) : 'other';
      return api.post('/api/parcel-values', {
        parcel_id: parcelId,
        tax_year: taxYear,
        value_type,
        value_source: source,
        date: date || null,
        appeal_id: appealId,
        forum: forum || null,
        event: event || null,
        market_land: num(land.market),
        market_imp: num(imp.market),
        market_pp: num(pp.market),
        market_total: marketTotal,
        ratio_land: num(land.ratio),
        ratio_imp: num(imp.ratio),
        ratio_pp: num(pp.ratio),
        assessed_land: num(land.assessed),
        assessed_imp: num(imp.assessed),
        assessed_pp: num(pp.assessed),
        assessed_total: assessedTotal,
      });
    },
    onSuccess: (result) => {
      const warnings = (result as { warnings?: string[] } | null)?.warnings ?? [];
      toast({ variant: 'success', message: 'Value added.' });
      for (const w of warnings) toast({ variant: 'warning', message: w });
      for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key });
      reset();
      onClose();
    },
    onError: (err) => {
      const raw = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      // A UNIQUE(parcel_id, tax_year, value_type) collision means this stage-slot
      // for the year is already keyed — point the user at editing it instead.
      const friendly = /unique|duplicate/i.test(raw)
        ? `A ${forum || 'value'} record already exists for ${taxYear}. Open its box to edit it instead.`
        : raw;
      toast({ variant: 'error', message: friendly });
    },
  });

  // (Operator feedback 8/6) NOTHING blocks saving a value except a save already in
  // flight. Source used to be required; adding the next stage of an appeal as it moves
  // is the common case and a missing source is not a reason to refuse the record.
  const canSubmit = !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) { reset(); onClose(); } }}
      title={`Add value · ${taxYear}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose(); }} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="primary" onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!canSubmit}>Save value</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {/* (Operator feedback 8/6) NO appeal selector. Adding a value IS adding the next
            stage of the appeal already in flight, so the appeal is DERIVED below — asking
            again made the operator re-answer a question the parcel already knows. Forum,
            event and date are the whole form. */}
        {/* Forum + Event + Date */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 'var(--sp-3)' }}>
          <Field label="Forum">
            <Select
              preserveOrder
              value={forum}
              onChange={(e) => { setForum(e.target.value); setEvent(''); }}
              options={[
                { value: '', label: forums.length ? '— Select —' : (templatesQuery.isLoading ? 'Loading…' : 'No ladder for this state') },
                ...forums.map((f) => ({ value: f.name, label: f.name })),
              ]}
            />
          </Field>
          <Field label="Event">
            <Select
              preserveOrder
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              options={[
                { value: '', label: forum ? '— Select —' : 'Pick a forum first' },
                ...events.map((ev) => ({ value: ev.name, label: ev.name })),
              ]}
            />
          </Field>
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>

        <Field label="Source">
          <Input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="e.g. Mail notice, Portal, Agent"
            invalid={source.trim() === ''}
          />
        </Field>

        {/* Market / Ratio / Assessed grid — computes both directions. */}
        <div>
          <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>
            Market · Ratio · Assessed
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 460 }}>
              <GridHeader />
              <GridInputRow label="Land" row={land} onChange={setLand} />
              <GridInputRow label="Improvement" row={imp} onChange={setImp} />
              <GridInputRow label="Personal" row={pp} onChange={setPp} />
              {/* Total row — derived, read-only; blended ratio is the keying check. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 'var(--sp-2)', alignItems: 'center', padding: 'var(--sp-2) 0', borderTop: '2px solid var(--color-border)' }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Total</span>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{money(marketTotal)}</span>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }} title="Blended ratio (assessed ÷ market)">
                  {blendedRatio ? blendedRatio.toFixed(4) : '—'}
                </span>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{money(assessedTotal)}</span>
              </div>
            </div>
          </div>
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Enter market → assessed derives (market × ratio); or enter assessed → market derives (assessed ÷ ratio).
          </p>
        </div>
      </div>
    </Modal>
  );
}

/** A labeled field wrapper matching the modal form style. */
function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
        {label}{required && <span style={{ color: 'var(--color-status-red)' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

function GridHeader() {
  const cell: React.CSSProperties = { fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 'var(--sp-2)', padding: '0 0 var(--sp-1)' }}>
      <span />
      <span style={cell}>Market</span>
      <span style={cell}>Ratio</span>
      <span style={cell}>Assessed</span>
    </div>
  );
}

/**
 * One editable grid row. Editing market or ratio recomputes assessed
 * (assessed = market × ratio); editing assessed recomputes market
 * (market = assessed ÷ ratio) — the "both directions" rule.
 */
function GridInputRow({ label, row, onChange }: { label: string; row: GridRow; onChange: (r: GridRow) => void }) {
  const onMarket = (market: string) => {
    const assessed = market !== '' ? String(Math.round(num(market) * num(row.ratio))) : '';
    onChange({ ...row, market, assessed });
  };
  const onRatio = (ratio: string) => {
    const assessed = row.market !== '' ? String(Math.round(num(row.market) * num(ratio))) : row.assessed;
    onChange({ ...row, ratio, assessed });
  };
  const onAssessed = (assessed: string) => {
    const r = num(row.ratio);
    const market = assessed !== '' && r !== 0 ? String(Math.round(num(assessed) / r)) : row.market;
    onChange({ ...row, assessed, market });
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 'var(--sp-2)', alignItems: 'center', padding: 'var(--sp-1) 0' }}>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>{label}</span>
      <Input type="number" size="sm" value={row.market} aria-label={`${label} market`} onChange={(e) => onMarket(e.target.value)} />
      <Input type="number" size="sm" step="0.0001" value={row.ratio} aria-label={`${label} ratio`} onChange={(e) => onRatio(e.target.value)} />
      <Input type="number" size="sm" value={row.assessed} aria-label={`${label} assessed`} onChange={(e) => onAssessed(e.target.value)} />
    </div>
  );
}

/**
 * Postal-code → lowercased full-state-name, so a parcel that stores "TX" resolves
 * the stage template keyed "Texas". Only the states with seeded default ladders
 * are needed here; an unmapped code simply finds no ladder (graceful — the Forum
 * select shows "No ladder for this state" and forum/event stay free/empty).
 */
